/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { plural } from '../../shared/format/plural';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import { StatusPill } from '../../shared/surface-cards/StatusPill';
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { formatMoney as formatPromoMoney } from '../../shared/format/public-api';
import type { CommissionEarning } from '../../commissions/domain/commissionTypes';
import { EARNING_STATUS_LABELS, commissionEarningTitle } from '../../commissions/domain/commissionTypes';
import { listCommissionEarningsForSale } from '../../commissions/infra/commissionsRepo';

type Props = {
  saleId: string;
  /** Si la venta está anulada, mostrar estado anulado */
  saleVoided?: boolean;
  /** ID del usuario logueado — para filtrar si canViewOthers es false */
  currentUserId?: string | null;
  /** Si puede ver comisiones de otros empleados */
  canViewOthers?: boolean;
  /** Si puede ver el módulo de comisiones */
  canViewCommissions?: boolean;
  /** Callback para navegar al detalle de una venta (para link desde earning) */
  onNavigateToSale?: (saleId: string) => void;
  /** Callback para navegar a Planes de Comisión */
  onNavigateToPlans?: () => void;
};

export function SaleDetailCommissionsSection({
  saleId,
  saleVoided = false,
  currentUserId,
  canViewOthers = true,
  canViewCommissions = true,
  onNavigateToSale,
  onNavigateToPlans,
}: Props) {
  const [earnings, setEarnings] = useState<CommissionEarning[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!canViewCommissions) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await listCommissionEarningsForSale({
        saleId,
        employeeUserId: canViewOthers ? null : currentUserId ?? null,
      });
      setEarnings(result);
    } catch (err) {
      setError('No se pudieron cargar las comisiones. Reintentá.');
      if (__DEV__) {
        console.warn('[SaleDetailCommissionsSection]', err);
      }
    } finally {
      setLoading(false);
    }
  }, [saleId, canViewCommissions, canViewOthers, currentUserId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!canViewCommissions) return null;

  const totalCommissions = earnings.reduce((sum, e) => sum + e.commissionAmount, 0);
  const voidedEarnings = earnings.filter((e) => e.status === 'voided');
  const pendingEarnings = earnings.filter((e) => e.status === 'pending');
  const settledEarnings = earnings.filter((e) => e.status === 'settled');
  const noPlanEarnings = earnings.filter((e) => e.commissionAmount === 0 && e.description?.toLowerCase().includes('sin plan'));

  return (
    <>
      <View style={styles.headerRow}>
        <Text style={modalExperience.formSectionTitle}>Comisiones</Text>
        {loading ? <ActivityIndicator size="small" color={ticketColors.ink3} /> : null}
      </View>
      <View style={styles.section}>
        {error ? (
          <Text style={styles.errorText}>{error}</Text>
        ) : earnings.length === 0 && !loading ? (
          <Text style={styles.caption}>
            {saleVoided ? 'Comisiones anuladas con la venta.' : 'Sin comisiones generadas.'}
          </Text>
        ) : (
          <>
            <View style={styles.summaryRow}>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Total</Text>
                <Text style={styles.summaryValue}>{formatPromoMoney(totalCommissions)}</Text>
              </View>
              {pendingEarnings.length > 0 ? (
                <View style={styles.summaryItem}>
                  <Text style={styles.summaryLabel}>Pendientes</Text>
                  <Text style={styles.summaryValue}>{pendingEarnings.length}</Text>
                </View>
              ) : null}
              {settledEarnings.length > 0 ? (
                <View style={styles.summaryItem}>
                  <Text style={styles.summaryLabel}>Liquidadas</Text>
                  <Text style={styles.summaryValue}>{settledEarnings.length}</Text>
                </View>
              ) : null}
              {voidedEarnings.length > 0 ? (
                <View style={styles.summaryItem}>
                  <Text style={styles.summaryLabel}>Anuladas</Text>
                  <Text style={styles.summaryValue}>{voidedEarnings.length}</Text>
                </View>
              ) : null}
            </View>
            {noPlanEarnings.length > 0 && !saleVoided ? (
              <View style={styles.noPlanHint}>
                <Ionicons name="information-circle-outline" size={16} color={ticketColors.warn} />
                <Text style={styles.noPlanHintText}>
                  {noPlanEarnings.length} {plural(noPlanEarnings.length, 'empleado', 'empleados')} sin plan de comisión asignado.
                  {onNavigateToPlans ? (
                    <Text
                      style={styles.noPlanHintLink}
                      onPress={onNavigateToPlans}
                    >
                      {' '}Asignar plan →
                    </Text>
                  ) : null}
                </Text>
              </View>
            ) : null}
            <View style={modalExperience.listContainer}>
              {earnings.map((e, index) => (
                <View
                  key={e.id}
                  style={[
                    modalExperience.listRow,
                    index === earnings.length - 1 && styles.listRowLast,
                  ]}
                >
                  <View style={styles.earningHeader}>
                    <Text style={modalExperience.listRowTitle}>
                      {formatPromoMoney(e.commissionAmount)}
                    </Text>
                    <StatusPill
                      label={EARNING_STATUS_LABELS[e.status] ?? e.status}
                      tone={e.status === 'voided' ? 'danger' : e.status === 'settled' ? 'success' : 'neutral'}
                      compact
                    />
                  </View>
                  <Text style={modalExperience.listMeta}>{e.employeeName}</Text>
                  <View style={styles.earningDetailRow}>
                    <Text style={styles.earningDetailText}>
                      Base: {formatPromoMoney(e.commissionableAmount)}
                      {e.ratePercent != null ? ` · ${e.ratePercent}%` : null}
                    </Text>
                    {e.description ? (
                      <Text style={styles.earningDetailText} numberOfLines={1}>
                        {commissionEarningTitle(e.description)}
                      </Text>
                    ) : null}
                  </View>
                </View>
              ))}
            </View>
          </>
        )}
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  section: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  caption: {
    fontSize: 13,
    color: ticketColors.ink3,
  },
  errorText: {
    fontSize: 13,
    color: ticketColors.bad,
  },
  summaryRow: {
    flexDirection: 'row',
    gap: spacing.md,
    marginBottom: spacing.sm,
  },
  summaryItem: {
    flex: 1,
  },
  summaryLabel: {
    fontSize: 11,
    color: ticketColors.ink3,
    fontWeight: '500',
  },
  summaryValue: {
    fontSize: 15,
    color: ticketColors.ink,
    fontWeight: '700',
  },
  listRowLast: {
    borderBottomWidth: 0,
  },
  earningHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  earningDetailRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginTop: 2,
  },
  earningDetailText: {
    fontSize: 11,
    color: ticketColors.ink3,
  },
  noPlanHint: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.xs,
    marginBottom: spacing.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 8,
    backgroundColor: ticketColors.paper2,
  },
  noPlanHintText: {
    flex: 1,
    fontSize: 12,
    color: ticketColors.ink2,
    flexWrap: 'wrap',
  },
  noPlanHintLink: {
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.gold,
  },
});
